import React from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ContextUsage } from '../../store/chatStore';

import { useTranslation } from 'react-i18next';

interface ContextUsageProgressProps {
  contextUsage?: ContextUsage;
  totalTokens?: number | null;
  className?: string;
}

export const ContextUsageProgress: React.FC<ContextUsageProgressProps> = ({
  contextUsage,
  totalTokens,
  className = '',
}) => {
  const { t } = useTranslation('pleri/chat');
  if (!contextUsage) {
    return null;
  }

  const { percentage, tokensUsed, maxContextSize } = contextUsage;

  // Determine the color based on usage percentage
  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) {
      return '#ef4444'; // red-500
    }
    if (percentage >= 75) {
      return '#eab308'; // yellow-500
    }
    return '#3b82f6'; // blue-500
  };

  const getTextColor = (percentage: number) => {
    if (percentage >= 90) {
      return 'text-red-500';
    }
    if (percentage >= 75) {
      return 'text-yellow-500';
    }
    return 'text-blue-500';
  };

  const progressColor = getProgressColor(percentage);
  const textColor = getTextColor(percentage);

  // Calculate the stroke-dasharray for the circular progress
  const cappedPercentage = Math.min(percentage, 100);

  return (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <div className={`relative cursor-pointer ${className}`}>
            <div className="flex items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1 dark:bg-gray-800">
              {/* Small circular indicator */}
              <div className="relative">
                <svg width="16" height="16" className="-rotate-90 transform">
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    className="text-gray-300"
                  />
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke={progressColor}
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 6}
                    strokeDashoffset={
                      2 * Math.PI * 6 -
                      (cappedPercentage / 100) * 2 * Math.PI * 6
                    }
                    className="transition-all duration-300 ease-in-out"
                  />
                </svg>
              </div>
              {/* Percentage text - only show when >= 75% */}
              <span className={`text-xs font-medium ${textColor}`}>
                {Math.round(Math.min(percentage, 100))}%
              </span>
            </div>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            id="search-app"
            side="top"
            align="end"
            sideOffset={8}
            style={{ zIndex: 100000000000 }}
          >
            <div className="relative w-64 rounded-lg bg-gray-800 px-3 py-2.5 text-xs text-white shadow-lg">
              {/* Header */}
              <div className="mb-2">
                <div className="font-semibold text-white">
                  {t('contextWindow')}
                </div>
                <div className="mt-0.5 leading-relaxed text-gray-400">
                  {t('howMuchOfTheModelMemoryThis')}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mb-2">
                <div className="mb-1 flex items-center justify-between">
                  <span
                    className={`font-medium ${
                      percentage >= 90
                        ? 'text-red-400'
                        : percentage >= 75
                          ? 'text-yellow-400'
                          : 'text-blue-400'
                    }`}
                  >
                    {t('percentUsed', { percent: Math.round(cappedPercentage) })}
                  </span>
                  <span className="text-gray-400 tabular-nums">
                    {t('usedOfMax', {
                      used: tokensUsed.toLocaleString(),
                      max: maxContextSize.toLocaleString(),
                    })}
                  </span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-gray-700">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${cappedPercentage}%`,
                      background:
                        percentage >= 90
                          ? 'linear-gradient(90deg, #f87171, #ef4444)'
                          : percentage >= 75
                            ? 'linear-gradient(90deg, #fbbf24, #eab308)'
                            : 'linear-gradient(90deg, #60a5fa, #3b82f6)',
                    }}
                  />
                </div>
              </div>

              {/* Status hint */}
              <div className="leading-relaxed text-gray-500">
                {percentage >= 100
                  ? t('theContextWindowIsFullStartA')
                  : percentage >= 90
                    ? t('almostFullOlderPartsOfTheConversation')
                    : percentage >= 75
                      ? t('gettingFullStartingANewChatWill')
                      : t('youHavePlentyOfRoomLeftIn')}
              </div>

              {/* Total tokens — only when data exists */}
              {totalTokens != null && (
                <div className="mt-2 flex items-center justify-between border-t border-gray-700 pt-2">
                  <span className="text-gray-400">{t('totalTokensUsed')}</span>
                  <span className="font-medium text-gray-300 tabular-nums">
                    {totalTokens >= 1_000_000
                      ? `${(totalTokens / 1_000_000).toFixed(1)}M`
                      : totalTokens >= 1_000
                        ? `${(totalTokens / 1_000).toFixed(1)}k`
                        : totalTokens.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            <Tooltip.Arrow className="fill-gray-800" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};
